import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { createProduct, updateProduct, fetchProductById } from "../../store/slices/productSlice";
import { PRODUCT_CATEGORIES } from "../../utils/constants";

const emptyForm = {
  name: "",
  description: "",
  category: "",
  price: "",
  stockQuantity: "",
  specifications: { material: "", size: "", moq: "" },
  tags: "",
  isActive: true,
};

const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProduct, loading, error } = useSelector((state) => state.products);

  const [form, setForm] = useState(emptyForm);
  const [mainImage, setMainImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && currentProduct && currentProduct._id === id) {
      setForm({
        name: currentProduct.name || "",
        description: currentProduct.description || "",
        category: currentProduct.category || "",
        price: currentProduct.price || "",
        stockQuantity: currentProduct.stockQuantity || "",
        specifications: {
          material: currentProduct.specifications?.material || "",
          size: currentProduct.specifications?.size || "",
          moq: currentProduct.specifications?.moq || "",
        },
        tags: currentProduct.tags?.join(", ") || "",
        isActive: currentProduct.isActive ?? true,
      });
      setImagePreview(currentProduct.image || "");
    }
  }, [isEdit, currentProduct, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("spec_")) {
      const key = name.replace("spec_", "");
      setForm((prev) => ({ ...prev, specifications: { ...prev.specifications, [key]: value } }));
    } else {
      setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMainImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!isEdit && !mainImage) {
      setSubmitError("Please select a product image.");
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("description", form.description);
    fd.append("category", form.category);
    if (form.price) fd.append("price", form.price);
    if (form.stockQuantity) fd.append("stockQuantity", form.stockQuantity);
    fd.append("specifications", JSON.stringify({
      material: form.specifications.material,
      size: form.specifications.size,
      moq: form.specifications.moq ? Number(form.specifications.moq) : undefined,
    }));
    if (form.tags) fd.append("tags", form.tags);
    fd.append("isActive", form.isActive);
    if (mainImage) fd.append("mainImage", mainImage);

    setSubmitting(true);
    try {
      if (isEdit) {
        await dispatch(updateProduct({ id, productData: fd })).unwrap();
      } else {
        await dispatch(createProduct(fd)).unwrap();
      }
      navigate("/admin/products");
    } catch (err) {
      setSubmitError(err || "Failed to save product.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEdit ? "Edit Product" : "Add Product"} - Admin</title>
      </Helmet>

      <div className="space-y-5">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/products")} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <FaArrowLeft size={14} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEdit ? "Edit Product" : "Add New Product"}
            </h1>
            <p className="text-gray-500 text-sm">Fill in the product details below</p>
          </div>
        </div>

        <div className="max-w-3xl">
          {(submitError || error) && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
              {submitError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8 space-y-6">
            {/* Image Upload */}
            <div>
              <label className="form-label">Product Image {!isEdit && <span className="text-red-500">*</span>}</label>
              <div className="mt-2 flex items-center gap-6">
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="h-24 w-24 object-cover rounded-lg border" />
                )}
                <label className="cursor-pointer bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center hover:border-blue-500 transition">
                  <FaUpload className="text-gray-400 text-2xl mb-2" />
                  <span className="text-sm text-gray-500">Click to upload image</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="form-label">Product Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="e.g. Premium Cotton T-Shirt"
              />
            </div>

            {/* Description */}
            <div>
              <label className="form-label">Description <span className="text-red-500">*</span></label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                required
                rows={4}
                className="form-textarea"
                placeholder="Describe the product (min 10 characters)"
              />
            </div>

            {/* Category */}
            <div>
              <label className="form-label">Category <span className="text-red-500">*</span></label>
              <select name="category" value={form.category} onChange={handleChange} required className="form-select">
                <option value="">Select Category</option>
                {PRODUCT_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Price & Stock */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Price (₹)</label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="form-input"
                  placeholder="Leave empty for 'Contact for Price'"
                />
              </div>
              <div>
                <label className="form-label">Stock Quantity</label>
                <input
                  type="number"
                  name="stockQuantity"
                  value={form.stockQuantity}
                  onChange={handleChange}
                  min="0"
                  className="form-input"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Specifications */}
            <div>
              <label className="form-label">Specifications</label>
              <div className="grid md:grid-cols-3 gap-4 mt-2">
                <input
                  type="text"
                  name="spec_material"
                  value={form.specifications.material}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Material"
                />
                <input
                  type="text"
                  name="spec_size"
                  value={form.specifications.size}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Size (e.g. S,M,L,XL)"
                />
                <input
                  type="number"
                  name="spec_moq"
                  value={form.specifications.moq}
                  onChange={handleChange}
                  min="1"
                  className="form-input"
                  placeholder="Min Order Qty"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="form-label">Tags</label>
              <input
                type="text"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. cotton, casual, summer (comma-separated)"
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="isActive"
                id="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="isActive" className="form-label mb-0">Active (visible on website)</label>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={submitting || loading}
                className="btn btn-primary px-8"
              >
                {submitting ? "Saving..." : isEdit ? "Update Product" : "Add Product"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/products")}
                className="btn btn-secondary px-8"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AdminProductForm;
